import React from 'react';
import { Grid, Box, CircularProgress, Alert } from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {
  DashboardMetricCard,
  ApiKeyDisplayCard,
  CodeExampleCard,
  RefreshButton,
  LoadingOverlay,
  OverviewSkeleton,
} from '../Components';
import { useApiData } from '../hooks/useApiData';
import { formatUsd } from '../utils/formatUsd';

const ApiOverviewPage = ({
  setActiveTab,
}: {
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { apiKeyData, usageData, loading, refreshing, error, refreshData } = useApiData();

  const handleManageCreditsClick = () => {
    setActiveTab(2);
  };

  const handleViewDetailsClick = () => {
    setActiveTab(1);
  };

  // Calculate metrics from API data
  const accountStatusMetrics = apiKeyData
    ? [
        {
          value: formatUsd(apiKeyData.credits_remaining),
          label: 'Credit Balance',
        },
        {
          value: apiKeyData.tier || 'Standard',
          label: 'Partner Tier',
        },
        {
          value: apiKeyData.status === 'active' ? 'Enabled' : 'Disabled',
          label: 'API Key',
        },
      ]
    : [];

  const usageSummaryMetrics = usageData
    ? [
        {
          value: formatUsd(usageData.total_cost),
          label: 'Total Spent',
        },
        {
          value: usageData.total_usage_count?.toString() || '0',
          label: 'API Calls',
        },
        {
          value:
            usageData.total_usage_count > 0
              ? `$${((usageData.total_cost || 0) / usageData.total_usage_count).toFixed(2)}`
              : '$0.00',
          label: 'Avg Cost / Call',
        },
      ]
    : [];

  const codeExample = `curl -X GET "https://crap0y5bx5.execute-api.us-east-2.amazonaws.com/prod/listings/metrics/all?listing_id=50027615" \\
-H "x-api-key: YOUR_API_KEY"`;

  const resourceLinks = [
    {
      text: 'API Documentation',
      href: 'https://documenter.getpostman.com/view/25155751/2sB3QRoSvW',
      external: true,
    },
    {
      text: 'API Pricing',
      href: 'https://airbtics.com/api-pricing',
      external: true,
    },
    {
      text: 'API terms & conditions',
      href: 'https://airbtics.notion.site/api-data-usage-caching-guidelines',
      external: true,
    },
    {
      text: 'Run in Postman',
      href:
        'https://elements.getpostman.com/view/import?collection=25155751-2d0965f3-fc41-48d5-8a30-0708e4adfb36-2sB3QRoSvW',
      external: true,
    },
  ];

  if (loading) {
    return <OverviewSkeleton />;
  }

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <RefreshButton onRefresh={refreshData} loading={refreshing} />
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Grid container spacing={3}>
        {/* Account Status Section */}
        <Grid item xs={12} md={6}>
          <LoadingOverlay loading={refreshing}>
            <DashboardMetricCard
              title="Account Status"
              icon={<AccountBalanceWalletIcon />}
              metrics={accountStatusMetrics}
              actionText="Manage Credits"
              onActionClick={handleManageCreditsClick}
            />
          </LoadingOverlay>
        </Grid>

        {/* Usage Summary Section */}
        <Grid item xs={12} md={6}>
          <LoadingOverlay loading={refreshing}>
            <DashboardMetricCard
              title="Usage Summary"
              icon={<TrendingUp />}
              metrics={usageSummaryMetrics}
              actionText="View Details"
              onActionClick={handleViewDetailsClick}
              timeframe="Last 30 days"
            />
          </LoadingOverlay>
        </Grid>

        {/* API Key Section */}
        <Grid item xs={12}>
          <LoadingOverlay loading={refreshing}>
            <ApiKeyDisplayCard
              apiKey={apiKeyData?.key_id || ''}
              onCopy={() => console.log('API key copied')}
              onToggleVisibility={() => console.log('API key visibility toggled')}
            />
          </LoadingOverlay>
        </Grid>

        {/* Quick Start Guide and Resources Section */}
        <Grid item xs={12}>
          <CodeExampleCard resourceLinks={resourceLinks} codeExample={codeExample} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiOverviewPage;
